import React from "react";
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import dayjs, { Dayjs } from "dayjs";

/**
 * Section shown on the class Create + Edit forms. Holds the CLASS FEE that
 * the old system used to ask for and the new system needs entered once per
 * class. Values are pushed into the parent formik so the outer form
 * submit picks them up automatically — no separate save button.
 *
 * The section is intentionally optional: a class may be saved without a
 * fee. Class-Wise students assigned to a fee-less class see a warning in
 * the assignment drawer telling the admin to set the fee first, rather
 * than silently billing $0.
 */
export interface ClassBillingSectionProps {
  fee: number | string | null;
  taxStatus: "exempt" | "applicable" | null;
  effectiveFrom: string | null; // YYYY-MM-DD

  // Only set on the Edit form; when present the section warns that the
  // change will apply from `effectiveFrom` forward and older invoices
  // won't be touched. Passing null hides the warning on Create.
  currentFeeFromServer: number | null;

  onChange: (patch: {
    fee?: number | null;
    taxStatus?: "exempt" | "applicable" | null;
    effectiveFrom?: string | null;
  }) => void;
}

const ClassBillingSection: React.FC<ClassBillingSectionProps> = ({
  fee,
  taxStatus,
  effectiveFrom,
  currentFeeFromServer,
  onChange,
}) => {
  const parsedFee = typeof fee === "number"
    ? fee
    : typeof fee === "string" && fee !== ""
      ? Number(fee)
      : NaN;

  const isEditingExistingFee = currentFeeFromServer !== null && currentFeeFromServer !== undefined;
  const feeChanged = isEditingExistingFee
    && !Number.isNaN(parsedFee)
    && parsedFee !== currentFeeFromServer;

  const dayjsValue: Dayjs | null = effectiveFrom ? dayjs(effectiveFrom) : null;

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: (t) => `1px solid ${t.palette.divider}`,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.02),
        p: { xs: 2, md: 2.5 },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha("#0ea5e9", 0.12),
            color: "#0369a1",
          }}
        >
          <PaymentsOutlinedIcon fontSize="small" />
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Billing &amp; Fee
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Set the class fee once. Class-Wise students inherit this amount; Monthly students are not billed for it.
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Class fee"
            size="small"
            fullWidth
            type="number"
            inputProps={{ min: 0, step: "0.01" }}
            value={fee ?? ""}
            onChange={(e) => {
              const raw = e.target.value;
              onChange({ fee: raw === "" ? null : Number(raw) });
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            helperText="Leave empty if this class is not billable."
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <TextField
              select
              size="small"
              label="Tax applicable"
              value={taxStatus ?? "exempt"}
              onChange={(e) =>
                onChange({
                  taxStatus: (e.target.value as "exempt" | "applicable") ?? "exempt",
                })
              }
              helperText={
                (taxStatus ?? "exempt") === "applicable"
                  ? "Tax will be added at invoice time."
                  : "No tax will be added to invoices for this class."
              }
            >
              <MenuItem value="exempt">Tax exempt</MenuItem>
              <MenuItem value="applicable">Tax applicable</MenuItem>
            </TextField>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Billing effective date"
              value={dayjsValue}
              onChange={(v) =>
                onChange({ effectiveFrom: v ? v.format("YYYY-MM-DD") : null })
              }
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  helperText: "Applies to future invoices only.",
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      {isEditingExistingFee && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Current fee on file: <strong>${(currentFeeFromServer ?? 0).toFixed(2)}</strong>
          </Typography>
        </Box>
      )}

      {feeChanged && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          Changing the fee affects <strong>future invoice generation only</strong>. Already generated invoices will not be modified.
        </Alert>
      )}
    </Box>
  );
};

export default ClassBillingSection;
